import SuperTokens from 'supertokens-web-js';
import Session from 'supertokens-web-js/recipe/session';
import ThirdPartyEmailPassword, {
  emailPasswordSignIn,
  sendPasswordResetEmail,
  submitNewPassword,
} from 'supertokens-web-js/recipe/thirdpartyemailpassword';
import { getAuthorisationURLWithQueryParamsAndSetState } from 'supertokens-web-js/recipe/thirdpartyemailpassword';
import {
  thirdPartySignInAndUp,
  emailPasswordSignUp,
} from 'supertokens-web-js/recipe/thirdpartyemailpassword';
import Passwordless, {
  getLoginAttemptInfo,
} from 'supertokens-web-js/recipe/passwordless';
import { goto, invalidateAll } from '$app/navigation';
import EmailVerification, {
  sendVerificationEmail,
  verifyEmail,
} from 'supertokens-web-js/recipe/emailverification';
import { toastCustom } from '$utils/toasts';
import {
  VITE_COOKIE_DOMAIN,
  VITE_API_BASE_URL,
  VITE_APP_BASE_URL,
  VITE_SUPERTOKENS_APP_NAME,
} from '$lib/env';
import { isKnownAPIError, type ThirdPartyId } from '$shared/lib';

export function supertokensInit() {
  SuperTokens.init({
    appInfo: {
      apiDomain: VITE_API_BASE_URL as string,
      apiBasePath: '/auth',
      appName: VITE_SUPERTOKENS_APP_NAME as string,
    },
    recipeList: [
      EmailVerification.init(),
      Session.init({
        autoAddCredentials: true,
        sessionTokenBackendDomain: (VITE_COOKIE_DOMAIN as string) ?? undefined,
      }),
      ThirdPartyEmailPassword.init(),
      Passwordless.init(),
    ],
  });
}

async function getErrorMessage(err: unknown, defaultMessage: string) {
  if (err instanceof Response) {
    const body = await err.json();
    return isKnownAPIError(body) ? body.message : defaultMessage;
  }
  return defaultMessage;
}

type SignupInput = {
  email: string;
  password: string;
  username: string;
  displayName: string;
};

type SignupErrors = {
  email: string[];
  password: string[];
  username: string[];
  displayName: string[];
};

export async function signupWithEmailAndPassword(
  input: SignupInput,
): Promise<SignupErrors> {
  const errors: SignupErrors = {
    email: [],
    password: [],
    username: [],
    displayName: [],
  };

  try {
    const response = await emailPasswordSignUp({
      formFields: Object.entries(input).map(([id, value]) => ({ id, value })),
    });

    if (response.status === 'FIELD_ERROR') {
      return response.formFields?.reduce(
        // biome-ignore lint/performance/noAccumulatingSpread: TODO: fix this
        (errors, field) => ({ ...errors, [field.id]: field.error }),
        errors,
      );
    }

    await invalidateAll();
    await sendEmailVerificationLink();
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while signing up. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }

  return errors;
}

type SigninErrors = {
  email: string[];
  password: string[];
};

export async function signinWithEmailAndPassword(
  email: string,
  password: string,
): Promise<SigninErrors> {
  const signinErrors: SigninErrors = {
    email: [],
    password: [],
  };

  try {
    const response = await emailPasswordSignIn({
      formFields: [
        {
          id: 'email',
          value: email,
        },
        {
          id: 'password',
          value: password,
        },
      ],
    });

    switch (response.status) {
      case 'FIELD_ERROR':
        for (const field of response.formFields ?? []) {
          if (field.id === 'email') {
            signinErrors.email = signinErrors.email.concat(field.error);
          }
          if (field.id === 'password') {
            signinErrors.password = signinErrors.password.concat(field.error);
          }
        }
        break;
      case 'WRONG_CREDENTIALS_ERROR':
        signinErrors.password = signinErrors.password.concat(
          'Email password combination is incorrect.',
        );
        break;
      default:
        window.location.href = '/';
    }
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while signing in. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }

  return signinErrors;
}

export async function oauthLogin(thirdPartyId: ThirdPartyId) {
  try {
    const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
      thirdPartyId,
      frontendRedirectURI: `${VITE_APP_BASE_URL}/oauth-callback/${thirdPartyId}`,
    });
    goto(authUrl);
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while signing in. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }
}

export async function handleOauthCallback() {
  try {
    const response = await thirdPartySignInAndUp();

    if (response.status === 'OK') {
      // Redirect to home page
      window.location.href = '/';
    } else {
      // SuperTokens requires that the third party provider
      // gives an email for the user. If that's not the case, sign up / in
      // will fail.

      // As a hack to solve this, you can override the backend functions to create a fake email for the user.
      console.log(
        'No email provided by social login. Please use another form of login',
      );
      goto('/signin');
    }
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while signing in. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
    goto('/signin');
  }
}

export async function logout() {
  await Session.signOut();
  window.location.href = '/signin';
}

export async function sendEmailVerificationLink() {
  try {
    const response = await sendVerificationEmail();
    if (response.status === 'EMAIL_ALREADY_VERIFIED_ERROR') {
      goto('/');
    } else {
      goto('/email-confirmation');
    }
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while verifying your email. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }
}

export async function consumeVerificationCode() {
  try {
    const response = await verifyEmail();
    if (response.status === 'EMAIL_VERIFICATION_INVALID_TOKEN_ERROR') {
      toastCustom({
        title: 'Error',
        description:
          'The verification link is expired or invalid. Please try again.',
        negative: true,
        position: 'bottom-center',
      });
      goto('/email-confirmation/failed');
    } else {
      goto('/email-confirmed');
    }
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while verifying your email. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }
}

type ResetPasswordErrors = {
  email: string[];
};

export async function sendResetPasswordLink(
  email: string,
): Promise<ResetPasswordErrors> {
  const errors: ResetPasswordErrors = { email: [] };

  try {
    const response = await sendPasswordResetEmail({
      formFields: [{ id: 'email', value: email }],
    });

    if (response.status === 'FIELD_ERROR') {
      errors.email =
        response.formFields
          ?.filter((field) => field.id === 'email')
          .map((field) => field.error) ?? [];
    }

    goto(`/reset-password/link-sent/${email}`);
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while sending the reset password link. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }

  return errors;
}

type NewPasswordErrors = {
  password: string[];
};

export async function newPasswordEntered(
  newPassword: string,
): Promise<NewPasswordErrors> {
  const errors: NewPasswordErrors = { password: [] };

  try {
    const response = await submitNewPassword({
      formFields: [{ id: 'password', value: newPassword }],
    });

    if (response.status === 'FIELD_ERROR') {
      errors.password =
        response.formFields
          ?.filter((field) => field.id === 'password')
          .map((field) => field.error) ?? [];
    }

    if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
      toastCustom({
        title: 'Error',
        description: 'Password reset link has expired or is invalid',
        negative: true,
        position: 'bottom-center',
      });
      goto('/signin');
    }

    goto('/reset-password/success');
  } catch (err: unknown) {
    toastCustom({
      title: 'Error',
      description: await getErrorMessage(
        err,
        'An unknown error occurred while resetting your password. Please try again.',
      ),
      negative: true,
      position: 'bottom-center',
    });
  }

  return errors;
}

export async function isThisSameBrowserAndDevice() {
  return (await getLoginAttemptInfo()) !== undefined;
}

// export async function sendMagicLink(email: string) {
//   try {
//     const response = await createCode({ email });
//   } catch (err: any) {
//     toastError(err);
//     await clearLoginAttemptInfo();
//     goto('/signin');
//   }
// }

// export async function hasInitialMagicLinkBeenSent() {
//   return (await getLoginAttemptInfo()) !== undefined;
// }

// export async function resendMagicLink() {
//   try {
//     const response = await resendCode();

//     if (response.status === 'RESTART_FLOW_ERROR') {
//       // this can happen if the user has already successfully logged in into
//       // another device whilst also trying to login to this one.

//       // we clear the login attempt info that was added when the createCode function
//       // was called - so that if the user does a page reload, they will now see the
//       // enter email / phone UI again.
//       await clearLoginAttemptInfo();
//       toastError('Login failed. Please try again');
//       goto('/signin', { invalidateAll: true });
//     }
//   } catch (err: any) {
//     toastError(err);
//   }
// }

// export async function handleMagicLinkClicked() {
//   try {
//     const response = await consumeCode();

//     if (response.status === 'OK') {
//       // we clear the login attempt info that was added when the createCode function
//       // was called since the login was successful.
//       await clearLoginAttemptInfo();
//       console.log('Consumed okay');
//       if (response.createdNewUser) {
//         // Add user to DB
//         goto('/email-confirmed');
//       } else {
//         goto('/');
//       }
//     } else {
//       // this can happen if the magic link has expired or is invalid
//       // or if it was denied due to security reasons in case of automatic account linking

//       // we clear the login attempt info that was added when the createCode function
//       // was called - so that if the user does a page reload, they will now see the
//       // enter email / phone UI again.
//       await clearLoginAttemptInfo();
//       toastCustom({
//         title: 'Error',
//         description: 'Login failed. Please try again',
//         negative: true,
//         position: 'bottom-center',
//       });
//       goto('/signin');
//     }
//   } catch (err: unknown) {
//     toastCustom({
//       title: 'Error',
//       description: await getErrorMessage(
//         err,
//         'An unknown error occurred while consuming the magic link. Please try again.',
//       ),
//       negative: true,
//       position: 'bottom-center',
//     });
//   }
// }
